// Pages publiques de la vie locale (lot L5-C1, M17, M21) : consultations de la collectivité (question,
// période, résultats agrégés après la clôture, sans répondant) avec la réponse d'un usager connecté selon
// la règle choisie, et calendrier des événements publiés (sans budget ni objet interne).
import { CATEGORIES_EVENEMENT, PUBLICS_CONSULTATION, REGLES_REPONSE, type ConsultationPublique, type ConsultationUsager, type EvenementPublic } from '@cc/contrats';
import { Bandeau, Bouton, EtatVide, LienBouton } from '@cc/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { messageErreur } from '../../../api/erreurs';
import { EtatRequete } from '../../../commun/EtatRequete';
import { dateHeure } from '../../../commun/format';
import { codeCollectiviteValide } from '../collectivite';
import { Bloc, EnTeteCitoyen, Ligne, Liste, TexteStatut } from '../commun';
import { useSessionUsager } from '../session/FournisseurSessionUsager';
import { CLES_PARTICIPATION, repondre, useConsultation, useConsultationsPubliques, useEvenementsPublics } from './api';
import styles from '../citoyen.module.css';

const ETATS = { a_venir: 'À venir', ouverte: 'Ouverte', close: 'Close' } as const;
const STATUTS_EVENEMENT = { a_venir: 'À venir', en_cours: 'En cours', passe: 'Passé', annule: 'Annulé' } as const;

export function PageConsultations() {
  const { code: brut } = useParams();
  const code = codeCollectiviteValide(brut);
  const liste = useConsultationsPubliques(code);
  if (!code) return <Navigate to="/citoyen" replace />;
  const elements = liste.data?.elements ?? [];
  return (
    <>
      <EnTeteCitoyen titre="Consultations des quartiers" surTitre={liste.data?.collectivite.nom} sousTitre="Questions posées aux habitants ; les résultats sont publiés en nombres de réponses, sans le nom des répondants." retour={`/citoyen/${code}`} libelleRetour="Accueil" />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} fonction="Les consultations" lignes={3}>
        {elements.length === 0 ? <EtatVide titre="Aucune consultation publiée"><p>Cette collectivité n&apos;a pas encore publié de consultation.</p></EtatVide> : (
          <Liste libelle="Consultations publiées">
            {elements.map((c) => <Ligne key={c.id} href={`/citoyen/${code}/consultations/${c.id}`} titre={`${c.question}${c.fictif ? ' (fictif)' : ''}`} sousTitre={`Quartier ${c.quartier} · jusqu'au ${dateHeure(c.fin)}`} droite={<TexteStatut libelle={ETATS[c.etat]} ton={c.etat === 'ouverte' ? 'info' : c.etat === 'close' ? 'ok' : 'neutre'} />} />)}
          </Liste>
        )}
      </EtatRequete>
    </>
  );
}

const estUsager = (c: ConsultationPublique | ConsultationUsager): c is ConsultationUsager => 'peut_repondre' in c;

export function PageConsultation() {
  const { code: brut, id } = useParams();
  const code = codeCollectiviteValide(brut);
  const { etat } = useSessionUsager();
  const fiche = useConsultation(code, id);
  const client = useQueryClient();
  const [choix, setChoix] = useState<number | null>(null);
  const [retour, setRetour] = useState<{ ok?: string; erreur?: string; enCours?: boolean }>({});
  if (!code) return <Navigate to="/citoyen" replace />;
  const c = fiche.data;
  const envoyer = async () => {
    if (choix === null || !c) return;
    setRetour({ enCours: true });
    try {
      await repondre(c.id, code, choix);
      setRetour({ ok: 'Votre réponse est enregistrée.' });
      await client.invalidateQueries({ queryKey: ['citoyen', 'consultation'] });
      await client.invalidateQueries({ queryKey: CLES_PARTICIPATION.consultations(code) });
    } catch (e) {
      setRetour({ erreur: messageErreur(e) });
    }
  };
  return (
    <>
      <EnTeteCitoyen titre={c ? c.question : 'Consultation'} sousTitre={c ? `${c.numero} · quartier ${c.quartier} · du ${dateHeure(c.debut)} au ${dateHeure(c.fin)}` : undefined} retour={`/citoyen/${code}/consultations`} libelleRetour="Consultations" />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} fonction="La consultation" lignes={3}>
        {c ? (
          <>
            <Bloc titre="Règles de la consultation">
              <p className={styles.aide}>{`${PUBLICS_CONSULTATION[c.public_cible]}. ${REGLES_REPONSE[c.regle_reponse]}. État : ${ETATS[c.etat]}.`}</p>
            </Bloc>
            {c.resultats ? (
              <Bloc titre="Résultats">
                <ul aria-label="Résultats agrégés">{c.resultats.map((r) => <li key={r.option}>{`${r.option} : ${r.reponses} réponse${r.reponses > 1 ? 's' : ''}`}</li>)}</ul>
                <p className={styles.aide}>{`${c.total_reponses ?? 0} réponse(s) au total. Aucune identité de répondant n'est publiée.`}</p>
              </Bloc>
            ) : <p className={styles.aide}>Les résultats sont publiés après la clôture.</p>}
            {etat !== 'connecte' && c.etat === 'ouverte' ? <LienBouton href="/citoyen/connexion" variante="principal">Se connecter pour répondre</LienBouton> : null}
            {estUsager(c) ? (
              <Bloc titre="Votre réponse">
                {c.mon_choix !== null ? <p className={styles.aide}>{`Vous avez répondu : ${c.options[c.mon_choix] ?? ''}.`}</p> : null}
                {c.peut_repondre ? (
                  <form onSubmit={(e) => { e.preventDefault(); void envoyer(); }}>
                    <fieldset>
                      <legend>{c.question}</legend>
                      {c.options.map((o, i) => (
                        <div key={o} className={styles.aide}>
                          <label><input type="radio" name="choix" value={i} checked={choix === i} onChange={() => setChoix(i)} /> {o}</label>
                        </div>
                      ))}
                    </fieldset>
                    {retour.ok ? <Bandeau type="succes">{retour.ok}</Bandeau> : null}
                    {retour.erreur ? <Bandeau type="erreur">{retour.erreur}</Bandeau> : null}
                    <Bouton type="submit" variante="principal" chargement={retour.enCours} disabled={choix === null}>Envoyer ma réponse</Bouton>
                  </form>
                ) : <p className={styles.aide}>{c.etat !== 'ouverte' ? 'La consultation n\'est pas ouverte.' : c.mon_choix !== null ? 'Une seule réponse par compte pour cette consultation.' : 'Cette consultation est réservée aux représentants du quartier en exercice.'}</p>}
              </Bloc>
            ) : null}
          </>
        ) : null}
      </EtatRequete>
    </>
  );
}

function LigneEvenement({ e }: { e: EvenementPublic }) {
  return (
    <Bloc titre={`${e.intitule}${e.fictif ? ' (fictif)' : ''}`} etiquette={CATEGORIES_EVENEMENT[e.categorie]}>
      <p className={styles.aide}>{`Du ${dateHeure(e.debut)} au ${dateHeure(e.fin)} · ${e.lieu}`}</p>
      <p className={styles.aide}>{e.texte_public}</p>
      <TexteStatut libelle={STATUTS_EVENEMENT[e.statut]} ton={e.statut === 'annule' ? 'attention' : e.statut === 'passe' ? 'neutre' : 'info'} />
    </Bloc>
  );
}

export function PageEvenementsPublics() {
  const { code: brut } = useParams();
  const code = codeCollectiviteValide(brut);
  const liste = useEvenementsPublics(code);
  if (!code) return <Navigate to="/citoyen" replace />;
  const elements = liste.data?.elements ?? [];
  const aVenir = elements.filter((e) => e.statut !== 'passe');
  const passes = elements.filter((e) => e.statut === 'passe');
  return (
    <>
      <EnTeteCitoyen titre="Agenda des événements" surTitre={liste.data?.collectivite.nom} sousTitre="Événements publiés par la collectivité." retour={`/citoyen/${code}`} libelleRetour="Accueil" />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} fonction="L'agenda" lignes={3}>
        {elements.length === 0 ? <EtatVide titre="Aucun événement publié"><p>Aucun événement n&apos;est annoncé pour le moment.</p></EtatVide> : (
          <>
            {aVenir.map((e) => <LigneEvenement key={e.id} e={e} />)}
            {passes.length > 0 ? <h2 className={styles.aide}>Événements passés</h2> : null}
            {passes.map((e) => <LigneEvenement key={e.id} e={e} />)}
          </>
        )}
      </EtatRequete>
    </>
  );
}
